import {Outlet, useLocation} from 'react-router-dom';
import {Suspense, useEffect, useRef} from 'react';
import {AuthGuard} from '@/matic/features/_shared/components/auth/AuthGuard';
import {MfaGate} from '@/matic/features/_shared/components/auth/MfaGate';
import {SidebarLayout} from '@/matic/features/_shared/components/layout/sidebar';
import {isChromelessPath} from '@/matic/features/_shared/components/layout/chromeless';
import {HideAppLoader} from '@/matic/lib/utils/hide-app-loader';
import {trackPageViewed} from '@/matic/lib/product-observability';

/**
 * ─────────────────────────────────────────────────────────────────────────
 * Convention: adding a "full-viewport" route
 * ─────────────────────────────────────────────────────────────────────────
 * Some pages (Scout's chat + document viewer) need the entire vertical
 * space — no top Header bar. To opt into that, add the path here and put
 * the route under `<ProtectedLayout />` like every other protected route.
 *
 * **Do NOT** create a new sibling layout to `<ProtectedLayout />` (e.g. a
 * `<ChromelessProtectedLayout />`). That was the original pattern and it
 * caused PR #450's regression: every navigation crossing the boundary
 * tore down and rebuilt the entire chrome (AuthGuard + MfaGate +
 * SidebarLayout + sidebar + Suspense), producing a full-window flash on
 * Lattice ↔ Scout (and any sidebar click into/out of Scout).
 *
 * Keeping a single outer layout means the heavy chrome stays mounted; only
 * the small `Header` subtree mounts/unmounts when crossing this boundary.
 *
 * Match by exact path or path-prefix — e.g. `'/scout'` matches `/scout`
 * and `/scout/anything`.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * REQUIRED: dismiss the #app-loader on every chromeless route
 * ─────────────────────────────────────────────────────────────────────────
 * Chrome routes auto-dismiss the boot overlay via `<HideAppLoader />` once
 * the page chunk resolves. Chromeless routes suppress this (see
 * `autoDismissAppLoader` below) so pages can control the exact moment the
 * overlay drops — avoiding a flash of a half-loaded UI on first paint.
 *
 * **Every chromeless page is therefore responsible for calling
 * `hideAppLoader()` itself**, once its first meaningful data has loaded.
 * Forgetting this causes the overlay to linger for the full 10-second
 * safety-net timeout in main.tsx — a very slow apparent load on deep links
 * and hard refreshes.
 *
 * Pattern (call on mount — auth is done and the chunk has resolved by the time
 * the component renders, so the overlay can drop immediately):
 *
 *   import {hideAppLoader} from '@/matic/lib/utils/hide-app-loader';
 *
 *   useEffect(() => { hideAppLoader(); }, []);
 *
 * Existing implementations: ScoutContainer, LibraryPage.
 *
 * The chromeless path list lives in
 * `features/_shared/components/layout/chromeless.ts` so the sidebar can share
 * the same predicate (it decides where the account avatar mounts).
 */

/**
 * Protected layout for authenticated routes.
 *
 * Layer 1 (Authentication): AuthGuard blocks until user is authenticated
 * Layer 2 (MFA): MfaGate blocks until MFA is verified (if tenant requires it)
 * Layer 3 (Layout): SidebarLayout provides app chrome (sidebar + header)
 *
 * Header visibility is route-driven: AI workspace routes (Scout) hide the
 * top Header for full viewport height, while everything else shows it. We
 * toggle via a single `hideHeader` prop so the outer auth/sidebar tree stays
 * mounted across navigations — preventing a full-screen flash when crossing
 * between chromeless and non-chromeless routes.
 *
 * The persistent #app-loader overlay (in index.html) is dismissed by
 * `<HideAppLoader />` once the lazy page chunk resolves — except on Scout,
 * which keeps it up until its Sources panel's lists have loaded (ScoutContainer
 * calls `hideAppLoader()` itself). Otherwise the boot M would hand off to a
 * still-loading Sources spinner — a visible "second loader" on login.
 */
export function ProtectedLayout() {
  const {pathname} = useLocation();
  const previousPathRef = useRef<string | undefined>(undefined);
  const hideHeader = isChromelessPath(pathname);
  // Scout dismisses the boot overlay itself (after its data loads), so don't
  // auto-dismiss it on chunk load for those routes.
  const autoDismissAppLoader = !isChromelessPath(pathname);

  useEffect(() => {
    trackPageViewed(pathname, previousPathRef.current);
    previousPathRef.current = pathname;
  }, [pathname]);

  return (
    <AuthGuard>
      <MfaGate>
        <SidebarLayout hideHeader={hideHeader}>
          <Suspense fallback={null}>
            {autoDismissAppLoader && <HideAppLoader />}
            <Outlet />
          </Suspense>
        </SidebarLayout>
      </MfaGate>
    </AuthGuard>
  );
}
